import { CellCoordsObj, CellSelection } from '../../../../../common/table/TableTypes';

import { getMaxCellSelection, getMinCellSelection } from './tableSelectionGeometryUtils';

/**
 * Returns the spreadsheet-style label for a zero-based column index (for example A, B, AA).
 */
const getColName = (col: number): string => {
    const res = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[col % 26];
    if (col <= 26) return res;

    return getColName(Math.floor(col / 26) - 1) + res;
};

/**
 * Returns the spreadsheet-style name for a single cell coordinate (for example A1, AB11).
 */
export const getCellCoordsName = (coords: CellCoordsObj): string => {
    const { row, col } = coords;

    return `${getColName(col)}${row + 1}`;
};

/**
 * Returns the spreadsheet-style name for a selection range.
 */
export const getCellRangeName = (cellSelection: CellSelection): string => {
    const { row: minRow, col: minCol } = getMinCellSelection(cellSelection);
    const { row: maxRow, col: maxCol } = getMaxCellSelection(cellSelection);

    if (minRow === maxRow && minCol === maxCol) {
        return getCellCoordsName({
            row: minRow,
            col: minCol,
        });
    }

    return `${getCellCoordsName({ row: minRow, col: minCol })}:${getCellCoordsName({ row: maxRow, col: maxCol })}`;
};

/**
 * Converts a spreadsheet-style column label into a zero-based column index.
 */
const parseCellColName = (colName: string): number => {
    const colNameUpper = colName.toUpperCase();
    let colNumber = 0;

    for (let i = 0; i < colNameUpper.length; i++) {
        const char = colNameUpper.charCodeAt(i);
        colNumber += (char - 64) * 26 ** (colNameUpper.length - i - 1);
    }

    return colNumber - 1;
};

/**
 * Parses a spreadsheet-style cell or range name into a Milanote cell selection tuple.
 */
export const parseCellName = (str: string): CellSelection | null => {
    const cellSelection = str.match(/^\$?([a-z]+)\$?([0-9]+)(?::\$?([a-z]+)\$?([0-9]+))?$/i);
    if (!cellSelection) return null;

    const [_, startColName, startRowName, endColName, endRowName] = cellSelection;

    const startRow = parseInt(startRowName, 10) - 1;
    const startCol = parseCellColName(startColName);
    const endRow = endRowName ? parseInt(endRowName, 10) - 1 : startRow;
    const endCol = endColName ? parseCellColName(endColName) : startCol;

    return [startRow, startCol, endRow, endCol];
};
